import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Linkedin, Twitter, Youtube, Facebook, Instagram, Users, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { COMPANY, GESIL_LOGO_URL } from "../../data/content";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getOrCreateVisitorId() {
  try {
    let id = localStorage.getItem("gesil_visitor_id");
    if (!id) {
      id =
        (crypto?.randomUUID && crypto.randomUUID()) ||
        `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("gesil_visitor_id", id);
    }
    return id;
  } catch {
    return `v_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

function formatNumber(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-IN").format(n);
}

export default function Footer() {
  const year = new Date().getFullYear();
  const [stats, setStats] = useState({ unique: null, visits: null });
  const [social, setSocial] = useState({
    linkedin_url: "",
    twitter_url: "",
    youtube_url: "",
    facebook_url: "",
    instagram_url: "",
    gesil_logo_url: "",
  });

  useEffect(() => {
    axios.get(`${API}/site-settings`).then((r) => setSocial(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    const sessionFlag = sessionStorage.getItem("gesil_tracked_session");
    const visitor_id = getOrCreateVisitorId();
    const url = `${API}/visitors/${sessionFlag ? "count" : "track"}`;
    const opts = sessionFlag
      ? axios.get(url)
      : axios.post(url, { visitor_id, path: window.location.pathname });
    opts
      .then((res) => {
        if (!alive) return;
        if (!sessionFlag) sessionStorage.setItem("gesil_tracked_session", "1");
        const { unique, visits } = res.data || {};
        setStats({ unique, visits });
      })
      .catch(() => {
        /* ignore — counter is non-blocking */
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-soft-blue text-[#0F172A] pt-24 pb-12 overflow-hidden border-t border-blue-100"
    >
      <div className="absolute inset-0 bg-grid-blue opacity-50 pointer-events-none" />
      <div className="absolute -top-24 right-0 h-72 w-72 bg-blue-200/40 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-6">
          <Link to="/" className="inline-block">
            <img
              src={social.gesil_logo_url || GESIL_LOGO_URL}
              alt="GESIL — Global e-Village Services India Ltd"
              className="h-24 w-auto object-contain"
            />
          </Link>
          <p className="text-[#475569] max-w-md leading-relaxed">
            {social.footer_about_text ||
              `${COMPANY.alt_tagline}. A trusted partner to governments and enterprises across India for AI-led digital transformation and citizen-centric service delivery.`}
          </p>
          <div className="flex items-center gap-3">
            {[
              { key: "linkedin_url", icon: Linkedin, label: "LinkedIn" },
              { key: "twitter_url", icon: Twitter, label: "Twitter / X" },
              { key: "youtube_url", icon: Youtube, label: "YouTube" },
              { key: "facebook_url", icon: Facebook, label: "Facebook" },
              { key: "instagram_url", icon: Instagram, label: "Instagram" },
            ]
              .filter((s) => social[s.key])
              .map(({ key, icon: Icon, label }) => (
                <a
                  key={key}
                  href={social[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-${key.replace("_url", "")}`}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-blue-200 hover:border-[#2563EB] hover:text-[#2563EB] text-[#0F172A] transition-colors"
                  aria-label={label}
                >
                  <Icon size={16} />
                </a>
              ))}
          </div>
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div className="overline">Quick Links</div>
          <ul className="space-y-2 text-[#475569]">
            <li>
              <Link to="/#services" className="hover:text-[#2563EB]">
                Services
              </Link>
            </li>
            <li>
              <Link to="/#projects" className="hover:text-[#2563EB]">
                Projects
              </Link>
            </li>
            <li>
              <Link to="/#ai" className="hover:text-[#2563EB]">
                AI Solutions
              </Link>
            </li>
            <li>
              <Link to="/#careers" className="hover:text-[#2563EB]">
                Careers
              </Link>
            </li>
            <li>
              <Link to="/#contact" className="hover:text-[#2563EB]">
                Contact
              </Link>
            </li>
            <li>
              <Link to="/admin/login" className="hover:text-[#2563EB]">
                CMS Login
              </Link>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-4 space-y-4">
          <div className="overline">Reach Us</div>
          <ul className="space-y-3 text-[#475569] text-sm">
            <li className="flex gap-3">
              <MapPin size={16} className="text-[#2563EB] shrink-0 mt-0.5" />
              <span>
                <b className="text-[#0F172A]">{COMPANY.head_office.label}:</b>{" "}
                {COMPANY.head_office.address}
              </span>
            </li>
            <li className="flex gap-3">
              <MapPin size={16} className="text-[#2563EB] shrink-0 mt-0.5" />
              <span>
                <b className="text-[#0F172A]">{COMPANY.corporate_office.label}:</b>{" "}
                {COMPANY.corporate_office.address}
              </span>
            </li>
            <li className="flex gap-3">
              <Mail size={16} className="text-[#2563EB] shrink-0 mt-0.5" />
              <a
                href={`mailto:${COMPANY.email_primary}`}
                className="hover:text-[#2563EB]"
              >
                {COMPANY.email_primary}
              </a>{" "}
              ·{" "}
              <a
                href={`mailto:${COMPANY.email_secondary}`}
                className="hover:text-[#2563EB]"
              >
                {COMPANY.email_secondary}
              </a>
            </li>
            <li className="flex gap-3">
              <Phone size={16} className="text-[#2563EB] shrink-0 mt-0.5" />
              <span>
                Toll Free{" "}
                <a
                  href={`tel:${COMPANY.toll_free.replace(/\s+/g, "")}`}
                  data-testid="footer-toll-free"
                  className="hover:text-[#2563EB] font-semibold text-[#0F172A]"
                >
                  {COMPANY.toll_free}
                </a>
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* Visitor counter strip */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 mt-14">
        <div className="rounded-2xl bg-white/80 backdrop-blur border border-blue-100 px-5 py-4 md:px-7 md:py-5 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-[0_8px_24px_-12px_rgba(37,99,235,0.18)]">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#2563EB] inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[#2563EB] animate-pulse" />
            Live Site Analytics
          </div>
          <div className="flex items-center gap-6 sm:gap-10">
            <div
              data-testid="visitor-unique-count"
              className="text-center"
              title="Unique digital visitors to this site"
            >
              <div className="flex items-center justify-center gap-2 text-[#1D4ED8]">
                <Users size={16} />
                <span className="font-display text-2xl md:text-3xl font-semibold text-[#0F172A] tabular-nums">
                  {formatNumber(stats.unique)}
                </span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#475569] mt-1">
                Unique Visitors
              </div>
            </div>
            <div className="h-10 w-px bg-blue-100" />
            <div
              data-testid="visitor-total-views"
              className="text-center"
              title="Total page views"
            >
              <div className="flex items-center justify-center gap-2 text-[#2563EB]">
                <Eye size={16} />
                <span className="font-display text-2xl md:text-3xl font-semibold text-[#0F172A] tabular-nums">
                  {formatNumber(stats.visits)}
                </span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#475569] mt-1">
                Total Page Views
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 mt-10 pt-8 border-t border-blue-100 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#475569]">
        <div>
          © {year} {COMPANY.name}. All rights reserved.
        </div>
        <div className="flex items-center gap-4">
          <span>GIGW Compliant</span>
          <span className="h-1 w-1 bg-blue-300 rounded-full" />
          <span>WCAG 2.1 AA</span>
          <span className="h-1 w-1 bg-blue-300 rounded-full" />
          <span>ISO Aligned</span>
        </div>
      </div>
    </footer>
  );
}
